import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SendIcon from '@mui/icons-material/Send';
import {
    Box,
    Grid,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { DataGrid, GridColDef, gridClasses } from '@mui/x-data-grid';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    useAiReservationRequestMutation,
    useGetAIConversationIdMutation,
    useGetProfileCompanyInfoQuery,
    useGetProfileDetailsQuery,
    useSubmitRecurringReservationMutation,
    useSubmitReservationMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import { CustomTextField } from '../../../../layout/styledcomponents/CustomTextField';
import UserRole from '../../../../shared/AuthRoles';
import toast from '../../../shared/Toast';
import CustomNoRowsOverlay from '../../../shared/NoRowsOverlayCustom';

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    '& .MuiTablePagination-toolbar': {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignContent: 'center',
    },
    '& .MuiTablePagination-displayedRows ': {
        paddingTop: '14px',
    },
    '& .MuiTablePagination-selectLabel': {
        paddingTop: '14px',
    },
    '& .MuiTablePagination-select': {
        paddingTop: '4px',
    },

    '& .MuiDataGrid-columnHeader': {
        fontSize: '12px',
        fontWeight: 900,
        color: '#000',
        borderBottom: `1px solid ${theme.palette.midGrey.main}`,
        paddingLeft: '0px',
    },
    [`& .${gridClasses.row}`]: {
        '&:hover, &.Mui-hovered': {
            backgroundColor: 'none',
            color: `${theme.palette.textAccent.main}`,

            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: `${theme.palette.neutral.main}`,

            color: `${theme.palette.textAccent.main}`,
            '&:hover, &.Mui-hovered': {
                backgroundColor: 'none',
                color: `${theme.palette.textAccent.main}`,

                '@media (hover: none)': {
                    backgroundColor: 'none',
                },
            },
        },
    },
}));

export default function AIReservationsAssistant() {
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 770px)');
    // Create a ref for the messages container
    const messagesEndRef = useRef<any>(null);
    const [requestAiReservation] = useSubmitRecurringReservationMutation();
    const [requestIndividualReservation] = useSubmitReservationMutation();
    const loggedInUser = UserRole();
    const { data: getCompanyDetails } = useGetProfileCompanyInfoQuery(
        `/owners/${loggedInUser?.company}`,
    );
    const { data: loggedInUserDetails } = useGetProfileDetailsQuery('/profile');
    const location = useLocation();
    const navigate = useNavigate();
    //------//
    const [moduleId, setModuleId] = useState(undefined);
    const [requestInput, setRequestInput] = useState('');
    const [pendingMessage, setPendingMessage] = useState('');
    const [nextPageFn, setNextPageFn] = useState<() => void>();

    // const self = loggedInUser?.sub.toString() === creatorId;
    //------//
    //API Calls
    const [getConversationId]: any = useGetAIConversationIdMutation();
    const [aiReservationRequest, { isLoading }]: any =
        useAiReservationRequestMutation();
    //------//
    //AI Response saved in state
    const [aiConversationId, setAiConversationId] = useState<
        string | undefined
    >();
    const [aiResponse, setAiResponse]: any = useState();
    const [aiDates, setAiDates]: any = useState();
    //Keep history of messages to display in chat
    const [messages, setMessages] = useState<any>([]);
    const [isTyping, setIsTyping] = useState(false);

    const theme = useTheme();

    //Checking what page, Reservations or from the experience to obtain moduleId if there is one
    useEffect(() => {
        setNextPageFn(undefined); // Reset nextPageFn when channel changes

        if (location?.state?.page !== undefined) {
            if (location?.state?.page === 'reservationPage') {
                setModuleId(undefined);
            } else {
                setModuleId(location?.state?.id);
            }
        }
        //initiate AI conversation ID
        initConversationId();
        setIsTyping(true);
        chatInitiated();
    }, [location]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const chatInitiated = () => {
        const message = {
            message: `${
                location?.state?.id !== undefined
                    ? `Hello, tell us when you would like to reserve ${location?.state?.name}?`
                    : `Hello, tell us what experience you would like to reserve?`
            }`,
            sentTime: 'just now',
            sender: '7DAY Assistant',
        };

        setTimeout(function () {
            setMessages(() => [message]);
            setIsTyping(false);
        }, 2000);
    };

    const initConversationId = () => {
        getConversationId().then((data: any) => {
            setAiConversationId(data?.data?.id);
        });
    };
    const handleInput = (event: { target: { value: string } }) => {
        //Save the input
        const message = event?.target?.value;
        setPendingMessage(message);
        setRequestInput(message);
    };

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevent default form behavior
            onSubmit(); // Trigger the submit action
        }
    };

    const onSubmit = () => {
        setPendingMessage('');
        //check if moduleId is undefined (if undefined the user has come from the /Reservation page and not the experience)
        const message = requestInput;
        const newMessage = {
            message,
            direction: 'outgoing',
            sender: loggedInUser?.sub.toString(),
        };
        setMessages((prevMessages: any) => [...prevMessages, newMessage]);
        setIsTyping(true);
        const element: any = document.getElementById('messages');
        element.scroll({
            top: 100,
            left: 100,
            behavior: 'smooth',
        });

        let requestBody = {};
        if (moduleId === undefined) {
            requestBody = {
                conversationId: aiConversationId,
                message: requestInput,
            };
        } else {
            requestBody = {
                conversationId: aiConversationId,
                message: requestInput,
                moduleId: moduleId,
            };
        }

        //Submit users request to AI. If data, display it to the user, if not initate the AI chat to get more information.
        aiReservationRequest(requestBody).then((data: any) => {
            setIsTyping(true);
            if (data) {
                //set the response to show the user in list format
                if (data?.error?.status === 400) {
                    //Here is where we will create the AI assistant Chat
                    setIsTyping(false);
                    const message = data?.error?.data.message;
                    const newMessage = {
                        message,
                        direction: 'just now',
                        sender: '7DAY Assistant',
                    };
                    setMessages((prevMessages: any) => [
                        ...prevMessages,
                        newMessage,
                    ]);
                    window.scrollTo(0, 500);
                } else {
                    setAiResponse(data?.data);
                    setAiDates(data?.data?.dates);

                    setIsTyping(false);
                }
            } else {
                setIsTyping(false);
            }
        });
    };

    const clearForm = () => {
        //clear the form if the user wants to start again

        setAiResponse();
        setRequestInput('');
        const message = {
            message: `${
                location?.state?.id !== undefined
                    ? `Hello, tell us when you would like to reserve ${location?.state?.name}?`
                    : `Hello, tell us what experience you would like to reserve?`
            }`,
            sentTime: 'just now',
            sender: '7DAY Assistant',
        };

        setMessages(() => [message]);

        //Get new conversation id
        initConversationId();
    };

    const onClickRequest = () => {
        //if user is okay with response, submit Reservation (note Frontend does not include dates that are actually unavailable)
        const submitedDates = aiDates?.filter(
            (value: any) => value?.isAvailable !== false,
        );

        const formatDates = submitedDates?.map((i: any) => {
            return {
                startDate: moment(i.startDate).format('YYYY-MM-DD'),
                endDate: moment(i.endDate).format('YYYY-MM-DD'),
            };
        });

        const baseRequestBody = {
            bookerType: 'owner',
            module: {
                id: aiResponse?.module?.id,
            },
            onBehalf: false,
            reason: aiResponse?.reason,
            notes: aiResponse?.notes,
            fees: [],
            policies: [],
            staffAttending: [],
            setMenus: [],
        };

        if (formatDates?.length === 1) {
            // if user has request just one date, we use POST /Reservation rather than POST /recurring-Reservation
            const requestBody = {
                ...baseRequestBody,
                startDate: formatDates[0]?.startDate,
                endDate: formatDates[0]?.endDate,
            };
            requestIndividualReservation(requestBody).then((data: any) => {
                if (data) {
                    if (data?.error) {
                        if (
                            data?.error?.data.error === 'booking_cap_exceeded'
                        ) {
                            toast.error(`${data?.error?.data.message}`);
                        } else {
                            toast.error(`Failed to submit Reservation`);
                        }
                    } else {
                        toast.success(`Reservation request submitted`);
                        navigate(
                            `/reservationRequest/submitted/${data?.data?.id}`,
                            {
                                state: {
                                    page: 'singleReservation',
                                    id: data?.data?.id,
                                },
                            },
                        );
                    }
                } else {
                    if (data?.error === 'booking_cap_exceeded') {
                        toast.error(`${data?.error?.data.message}`);
                    } else {
                        toast.error(`Failed to submit Reservation`);
                    }
                }
            });
        } else {
            const requestBody = {
                ...baseRequestBody,
                dates: formatDates,
            };
            requestAiReservation(requestBody).then((data: any) => {
                if (data) {
                    const recurringId = data?.data?.map((i: any) => {
                        return i.recurringId;
                    });

                    if (data?.error) {
                        if (
                            data?.error?.data.error === 'booking_cap_exceeded'
                        ) {
                            toast.error(`${data?.error?.data.message}`);
                        } else {
                            toast.error(`Failed to submit Reservation`);
                        }
                    } else {
                        toast.success(`Reservation request submitted`);
                        navigate(
                            `/reservationRequest/submitted/ai-assistant/${recurringId[0]}`,
                            {
                                state: {
                                    page: 'recurringReservation',
                                    id: recurringId[0],
                                },
                            },
                        );
                    }
                } else {
                    if (data?.error === 'booking_cap_exceeded') {
                        toast.error(`${data?.error?.data.message}`);
                    } else {
                        toast.error(`Failed to submit Reservation`);
                    }
                }
            });
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'moduleId',
            headerName: 'Experience',
            width: isMobile ? 150 : isIpad ? 150 : 200,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return params?.row.isAvailable === true ? (
                    <Typography>{aiResponse?.module.name}</Typography>
                ) : (
                    <Typography color='red'>
                        {aiResponse?.module.name}
                    </Typography>
                );
            },
        },
        {
            field: 'moduleType',
            headerName: 'Type',
            width: isMobile ? 150 : isIpad ? 100 : 100,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return params?.row.isAvailable === true ? (
                    <Typography>
                        {aiResponse?.module?.type.charAt(0).toUpperCase() +
                            aiResponse?.module?.type.slice(1)}
                    </Typography>
                ) : (
                    <Typography color='red'>
                        {aiResponse?.module?.type.charAt(0).toUpperCase() +
                            aiResponse?.module?.type.slice(1)}
                    </Typography>
                );
            },
        },

        {
            field: 'startDate',
            headerName: 'Start Date',
            width: isMobile ? 150 : isIpad ? 150 : 100,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return params?.row.isAvailable === true ? (
                    <Typography>
                        {moment(params?.value).format('DD/MM/YYYY')}
                    </Typography>
                ) : (
                    <Typography color='red'>
                        {moment(params?.value).format('DD/MM/YYYY')}
                    </Typography>
                );
            },
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            width: isMobile ? 150 : isIpad ? 150 : 100,
            type: 'Date',
            sortable: false,
            renderCell: (params) => {
                return params?.row.isAvailable === true ? (
                    <Typography>
                        {moment(params?.value).format('DD/MM/YYYY')}
                    </Typography>
                ) : (
                    <Typography color='red'>
                        {moment(params?.value).format('DD/MM/YYYY')}
                    </Typography>
                );
            },
        },
        {
            field: 'company',
            headerName: 'Company',
            width: isMobile ? 150 : isIpad ? 150 : 200,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return params?.row.isAvailable === true ? (
                    <Typography>{getCompanyDetails?.name}</Typography>
                ) : (
                    <Typography color='red'>
                        {getCompanyDetails?.name}
                    </Typography>
                );
            },
        },
        {
            field: 'vistType',
            headerName: 'Visit Type',
            width: isMobile ? 150 : isIpad ? 150 : 80,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return params?.row.isAvailable === true ? (
                    <Typography>Blocked</Typography>
                ) : (
                    <Typography color='red'>Blocked</Typography>
                );
            },
        },
        {
            field: 'reason',
            headerName: 'Reservation Reason',
            width: isMobile ? 150 : isIpad ? 150 : 350,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return params?.row.isAvailable === true ? (
                    <Typography>
                        {aiResponse?.reason.charAt(0).toUpperCase() +
                            aiResponse?.reason.slice(1)}
                    </Typography>
                ) : (
                    <Typography color='red'>
                        {aiResponse?.reason.charAt(0).toUpperCase() +
                            aiResponse?.reason.slice(1)}
                    </Typography>
                );
            },
        },
        {
            field: 'bookedBy',
            headerName: 'Reserved By',
            width: isMobile ? 150 : isIpad ? 150 : 150,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return params?.row.isAvailable === true ? (
                    <Typography>
                        {loggedInUserDetails?.firstName}{' '}
                        {loggedInUserDetails?.lastName}
                    </Typography>
                ) : (
                    <Typography color='red'>
                        {loggedInUserDetails?.firstName}{' '}
                        {loggedInUserDetails?.lastName}
                    </Typography>
                );
            },
        },
        {
            field: 'isAvailable',
            headerName: 'Availability',
            width: isMobile ? 150 : isIpad ? 150 : 150,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                return params?.value === true ? (
                    <Typography>Available</Typography>
                ) : (
                    <Typography color='red'>Unavailable</Typography>
                );
            },
        },
        {
            field: 'action',
            headerName: '',
            width: isMobile ? 50 : isIpad ? 50 : 50,
            sortable: false,
            renderCell: (params) => {
                const onClick = () => {
                    const currentRowId = params?.id;

                    const removeReservation = aiDates?.filter(
                        (value: any) => value?.id !== currentRowId,
                    );
                    setAiDates(removeReservation);
                };

                return params?.row.isAvailable === true ? (
                    <CustomIconButton
                        sx={{
                            backgroundColor: 'transparent',
                            width: '25px',
                            height: '25px',
                            '&:hover': {
                                cursor: 'pointer',
                                color: theme.palette.textAccent.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                        onClick={onClick}
                    >
                        <DeleteForeverIcon
                            fontSize='small'
                            sx={{ fontSize: '16px' }}
                        />
                    </CustomIconButton>
                ) : (
                    <CustomIconButton
                        sx={{
                            backgroundColor: 'transparent',
                            width: '25px',
                            height: '25px',
                            '&:hover': {
                                cursor: 'pointer',
                                color: 'red',
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                        onClick={onClick}
                    >
                        <DeleteForeverIcon
                            fontSize='small'
                            sx={{ fontSize: '16px' }}
                        />
                    </CustomIconButton>
                );
            },
        },
    ];

    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!nextPageFn) return;

        const el = e.currentTarget;

        const top = el.scrollHeight - el.clientHeight;
        const scroll = -el.scrollTop; // reversed because of the flex direction

        if (top - scroll <= 1) {
            nextPageFn();
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                position: 'relative',
                minHeight:
                    'calc(100vh - 160px - var(--app-prompt-banner-height, 0px))',
            }}
        >
            {aiResponse === undefined ? (
                <Grid
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    <Stack
                        spacing={2}
                        flex={1}
                        onScroll={onScroll}
                        sx={{
                            paddingTop: '50px',
                        }}
                    >
                        {messages?.map((message: any) => {
                            const isUserMessage =
                                loggedInUser?.sub.toString() ===
                                message?.sender;

                            return (
                                <Box
                                    display='flex'
                                    flexDirection='column'
                                    width='fit-content'
                                    maxWidth='90%'
                                    alignSelf={
                                        isUserMessage
                                            ? 'flex-end'
                                            : 'flex-start'
                                    }
                                    alignItems={
                                        isUserMessage
                                            ? 'flex-end'
                                            : 'flex-start'
                                    }
                                >
                                    <Box
                                        id='messages'
                                        bgcolor={
                                            isUserMessage
                                                ? 'chatComponent.userBackground'
                                                : 'chatComponent.assistantBackground'
                                        }
                                        color={
                                            isUserMessage
                                                ? theme.palette.primary
                                                      .contrastText
                                                : 'black'
                                        }
                                        sx={{
                                            py: 1,
                                            px: 2,
                                            borderRadius: 4.5,
                                            ...(isUserMessage
                                                ? {
                                                      borderBottomRightRadius: 0,
                                                  }
                                                : {
                                                      borderBottomLeftRadius: 0,
                                                  }),
                                        }}
                                    >
                                        <Typography
                                            color={
                                                isUserMessage
                                                    ? 'chatComponent.userText'
                                                    : 'chatComponent.assistantText'
                                            }
                                            sx={{
                                                whiteSpace: 'pre-line',
                                            }}
                                        >
                                            {' '}
                                            {message.message}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant='subtitle2'
                                        color='textSecondary'
                                        sx={{
                                            fontSize: '10px',
                                            padding: '5px',
                                        }}
                                    >
                                        {message?.sender === '7DAY Assistant'
                                            ? message?.sender
                                            : 'You'}
                                    </Typography>
                                </Box>
                            );
                        })}

                        {isTyping ? (
                            <Box
                                display='flex'
                                flexDirection='row'
                                width='fit-content'
                                maxWidth='90%'
                                alignSelf={'flex-start'}
                                alignItems={'flex-start'}
                                gap={0.3}
                                pt={1}
                            >
                                {' '}
                                <Skeleton
                                    variant='circular'
                                    width={8}
                                    height={8}
                                    sx={{
                                        bgcolor: theme.palette.lightGrey.main,
                                    }}
                                />
                                <Skeleton
                                    variant='circular'
                                    width={8}
                                    height={8}
                                    sx={{
                                        bgcolor: theme.palette.lightGrey.main,
                                    }}
                                />
                                <Skeleton
                                    variant='circular'
                                    width={8}
                                    height={8}
                                    sx={{
                                        bgcolor: theme.palette.lightGrey.main,
                                    }}
                                />
                            </Box>
                        ) : null}
                        {/* Reference for scrolling */}
                        <div ref={messagesEndRef} />
                    </Stack>

                    <Box
                        display='flex'
                        gap={1}
                        sx={{
                            backgroundColor: 'background.default',
                            bgcolor: 'background.default',
                            width: '100%',
                            flexShrink: 0,
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 10,
                            pt: 1,
                            pb: 2,
                        }}
                    >
                        <Grid
                            sx={{
                                width: '95%',
                            }}
                        >
                            {' '}
                            <CustomTextField
                                type='text'
                                onKeyDown={handleKeyDown}
                                placeholder={
                                    pendingMessage !== ''
                                        ? ''
                                        : moduleId !== undefined
                                          ? 'i.e Book every Tuesday from the 1st of October until the end of March between 9am and 5pm for maintenance'
                                          : 'i.e Book the Resort every Tuesday from the 1st of October until the end of March between 9am and 5pm for maintenance'
                                }
                                fullWidth
                                color='dark'
                                onChange={handleInput}
                                value={pendingMessage}
                                autoComplete='off'
                                inputProps={{
                                    backgroundColor: 'white',
                                    style: {
                                        backgroundColor: 'white',
                                    },
                                }}
                            />
                        </Grid>
                        <Grid sx={{ width: 'auto' }}>
                            <CustomButton
                                size='small'
                                color='secondary'
                                variant='contained'
                                onClick={onSubmit}
                                disabled={isLoading}
                                disableElevation
                                disableRipple
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        color: theme.palette.textAccent
                                            .contrastText,
                                        backgroundColor:
                                            theme.palette.textAccent.main,
                                    },
                                }}
                            >
                                <SendIcon />
                            </CustomButton>
                        </Grid>
                    </Box>
                </Grid>
            ) : (
                <Grid
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        paddingTop: '30px',
                        paddingBottom: '30px',
                    }}
                >
                    <Grid sx={{ height: '5%' }}>
                        {' '}
                        <Typography
                            component='h4'
                            sx={{
                                textAlign: 'left',
                                fontWeight: 900,
                                paddingBottom: '30px',
                            }}
                        >
                            Results
                        </Typography>
                    </Grid>

                    <Grid sx={{ height: '80%' }}>
                        {' '}
                        <StripedDataGrid
                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 0
                                    ? 'even'
                                    : 'odd'
                            }
                            sx={{
                                boxShadow: 0,
                                border: 0,
                                '& .MuiDataGrid-cell': {
                                    padding: '0',
                                },
                            }}
                            disableColumnFilter
                            disableColumnSelector
                            disableColumnMenu
                            disableDensitySelector
                            disableRowSelectionOnClick
                            rows={aiDates ? aiDates : []}
                            columns={columns}
                            getRowId={(row: any) => row?.id}
                            slots={{
                                noRowsOverlay: CustomNoRowsOverlay,
                            }}
                        />
                    </Grid>
                    <Grid
                        sx={{
                            display: 'flex',
                            width: '100%',
                            paddingTop: '50px',
                        }}
                    >
                        <Grid
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                width: '50%',
                            }}
                        >
                            <CustomButton
                                id='resetForm'
                                variant='outlined'
                                color='primary'
                                onClick={() => clearForm()}
                            >
                                Start Over
                            </CustomButton>
                        </Grid>
                        <Grid
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                width: '50%',
                            }}
                        >
                            <CustomButton
                                id='submitAiReservation'
                                variant='contained'
                                color='primary'
                                // loading={isLoading}
                                disabled={isLoading}
                                onClick={() => onClickRequest()}
                                startIcon={<AddCircleIcon />}
                            >
                                Reserve
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
